import { PrimariaApi } from "@uxland/primary-shell";
import { startOfDay } from "date-fns";
import { LitElement, css, html, unsafeCSS } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { lazyInject } from "../../../../infrastructure/ioc/container";
import { TYPES } from "../../../../infrastructure/ioc/types";
import { connectedProperty } from "../../../../infrastructure/state/connected-property";
import { isAnyCollectionBusySelector, isSomeCollectionLoadedAndOtherLoading } from "../../../add/handle-busy/selectors";
import { IActivityHistoryGroup } from "../../../domain/model";
import { activityHistorySearchStringSelector } from "../../../search/selectors";
import { activityHistoryGroupedItems } from "../../../selectors";
import { getGroupMostRecentDate } from "../../sort-history-items-by-date/sort-history-items-by-date";
import { estimateGroupHeight } from "./estimate-group-height";
import styles from "./styles.css?inline";
import { template } from "./template";
import { VirtualScroller, VirtualScrollerState } from "./virtual-scroller";

//@ts-ignore
@customElement("activity-history-timeline")
export class ActivityHistoryTimeline extends LitElement {
  render() {
    return html`
    ${template(this)}
  `;
  }

  static styles = css`
    ${unsafeCSS(styles)}
  `;

  @connectedProperty(activityHistoryGroupedItems)
  historyGroups: IActivityHistoryGroup[];

  @connectedProperty(isSomeCollectionLoadedAndOtherLoading)
  isSomeCollectionLoadedAndOtherLoading: boolean;

  @connectedProperty(isAnyCollectionBusySelector)
  isAnyCollectionBusy: boolean;

  @connectedProperty(activityHistorySearchStringSelector)
  searchString: string;

  @lazyInject(TYPES.primaryApi)
  api: PrimariaApi;

  @query(".virtual-container") private _virtualContainer!: HTMLElement;
  @query(".virtual-viewport") private _viewport!: HTMLElement;

  // Virtual scroller instance and state
  private _virtualScroller?: VirtualScroller<IActivityHistoryGroup>;
  private _cleanupVirtualScroller?: () => void;

  @state()
  _virtualScrollerState: VirtualScrollerState = {
    virtualItems: [],
    totalHeight: 0,
  };

  @state()
  _canShowNoDataMessage = false;

  private _noDataMessageTimer?: ReturnType<typeof setTimeout>;

  connectedCallback(): void {
    super.connectedCallback();
    // Start timer to allow showing "no data" message after 5 seconds
    this._noDataMessageTimer = setTimeout(() => {
      this._canShowNoDataMessage = true;
    }, 2000);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._cleanupVirtualScroller?.();
    if (this._noDataMessageTimer) {
      clearTimeout(this._noDataMessageTimer);
    }
  }

  private _lastViewportElement?: HTMLElement;

  async updated(changedProperties) {
    this._virtualScroller?.willUpdate();

    if (changedProperties.has("historyGroups") && this.historyGroups?.length) {
      // Resolver los elementos antes de actualizar el template
      await this.prepareComponents(this.historyGroups);
      this._updateVirtualScroller();

      // Esperar a que el DOM esté completamente actualizado
      await this.updateComplete;
    }

    // Setup virtualizer when viewport is available
    if (this._viewport && !this._virtualScroller) {
      setTimeout(() => this._setupVirtualScroller(), 0);
    }

    // Detect if viewport was remounted (new element after being removed from DOM)
    if (this._viewport && this._virtualScroller && this._viewport !== this._lastViewportElement) {
      this._cleanupVirtualScroller?.();
      this._virtualScroller = undefined;
      setTimeout(() => this._setupVirtualScroller(), 0);
    }

    // Track current viewport element
    if (this._viewport) {
      this._lastViewportElement = this._viewport;
    }
  }

  async prepareComponents(historyGroups) {
    for (const group of historyGroups) {
      // Resolver items del grupo principal
      group.items = await Promise.all(
        group.items.map(async (item) => ({
          ...item,
          component: await item.componentFactory({ item }),
        })),
      );

      // Resolver subgrupos
      for (const subGroup of group.subGroups) {
        subGroup.items = await Promise.all(
          subGroup.items.map(async (item) => ({
            ...item,
            component: await item.componentFactory({ item }),
          })),
        );
      }
    }
  }

  highlightMatch(text: string, searchString: string): string {
    if (!this.searchString || this.searchString.length < 2) return text;
    const regex = new RegExp(`(${searchString})`, "gi");
    return text.replace(regex, `<span class="highlight">$1</span>`);
  }

  highlighted(text?: string) {
    return text ? unsafeHTML(this.highlightMatch(text, this.searchString)) : null;
  }

  // Public API: scroll the timeline to a given date ("Anar a data").
  //
  // The list is sorted newest -> oldest by `getGroupMostRecentDate` (the same key
  // is used here so the mapping date->index is monotonic). We compare at *day*
  // granularity because `selectedDate` is midnight while group dates carry a time
  // of day. We land on the first (topmost) group whose day is on or before the
  // requested day — i.e. the requested day if it has entries, otherwise the
  // closest earlier day (newer entries above, older below). Using an absolute
  // nearest-timestamp search here is what produced the "jumps to another year"
  // and "lands on the day before" bugs.
  _scrollIntoDate(selectedDate: number) {
    if (!this._virtualScroller || !this._viewport) return;

    const groups = this.historyGroups ?? [];
    if (!groups.length) return;

    const targetDay = startOfDay(selectedDate).getTime();
    const dayOf = (group: IActivityHistoryGroup) => startOfDay(getGroupMostRecentDate(group)).getTime();

    let index = groups.findIndex((group) => dayOf(group) <= targetDay);
    // Requested day is older than every entry -> go to the oldest group.
    if (index === -1) index = groups.length - 1;

    // Jump near the target using the virtualizer's estimated offset so it
    // renders the window around `index`. We set scrollTop directly (NOT
    // scrollToIndex) to avoid the reconcile loop that re-scrolls every frame and
    // froze the UI. The estimate leaves tall groups mid-screen, so we then align
    // the real group element's top to the viewport top in a few bounded passes.
    this._viewport.scrollTop = this._virtualScroller.getOffsetForIndex(index);
    this._alignGroupTop(index, 0);
  }

  // Align the top of group `index`'s rendered element with the top of the
  // viewport, using its real on-screen position (not the size estimate, which
  // is off for very tall visits). Bounded to a few frames so it converges
  // without ever turning into a scroll loop.
  private _alignGroupTop(index: number, attempt: number) {
    if (attempt >= 4 || !this._viewport) return;
    requestAnimationFrame(() => {
      const viewport = this._viewport;
      if (!viewport) return;
      const el = this.renderRoot?.querySelector(`.visit[data-index="${index}"]`) as HTMLElement | null;
      if (!el) {
        // Window not rendered around the target yet — give it another frame.
        this._alignGroupTop(index, attempt + 1);
        return;
      }
      const delta = Math.round(el.getBoundingClientRect().top - viewport.getBoundingClientRect().top);
      if (Math.abs(delta) <= 1) return; // aligned
      viewport.scrollTop += delta;
      this._alignGroupTop(index, attempt + 1);
    });
  }

  // === VIRTUAL SCROLLER LOGIC ===

  private _setupVirtualScroller() {
    if (!this._viewport || this._virtualScroller) return;

    this._virtualScroller = new VirtualScroller({
      items: this.historyGroups || [],
      overscan: 8,
      estimateItemHeight: (index) => estimateGroupHeight(this.historyGroups?.[index]),
    });

    this._cleanupVirtualScroller = this._virtualScroller.setup(this._viewport, (state) => {
      this._virtualScrollerState = state;
      this.requestUpdate();

      if (this.api?.broker && this.searchString) {
        this.api.broker.publish(this.api.broker.events.activityHistory.searched, {
          searchQuery: this.searchString,
        });
      }
    });

    this._virtualScrollerState = this._virtualScroller.getState();
  }

  private _updateVirtualScroller() {
    if (!this._virtualScroller || !this.historyGroups?.length) return;

    this._virtualScroller.updateItems(this.historyGroups, (state) => {
      this._virtualScrollerState = state;
      this.requestUpdate();

      if (this.api?.broker && this.searchString) {
        this.api.broker.publish(this.api.broker.events.activityHistory.searched, {
          searchQuery: this.searchString,
        });
      }
    });

    this._virtualScrollerState = this._virtualScroller.getState();
  }

  // Pairs each visible virtual item with its absolute index in historyGroups.
  // The index is required so the template can stamp `data-index` on every
  // `.visit`, which is how the virtualizer measures each item's real height.
  get _visibleRows(): { index: number; group: IActivityHistoryGroup }[] {
    return this._virtualScrollerState.virtualItems
      .map((vi) => ({ index: vi.index, group: this.historyGroups?.[vi.index] }))
      .filter((row): row is { index: number; group: IActivityHistoryGroup } => Boolean(row.group));
  }

  get _containerOffset() {
    const firstItem = this._virtualScrollerState.virtualItems[0];
    return firstItem?.start ?? 0;
  }

  get _totalHeight() {
    return this._virtualScrollerState.totalHeight;
  }

  _measureElement = (el: Element | undefined) => {
    if (el) this._virtualScroller?.measureElement(el as HTMLElement);
  };

  _onScroll = () => {
    // Virtual scroller handles this internally
  };
}
